import { AnimatePresence, motion } from "framer-motion";
import { Check, Pencil, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { TEAM_PALETTE } from "./data.js";
import { cn } from "./cn.js";

const BUTTON_3D_BASE = {
  idleDepth: 1.5,
  hoverDepth: 3,
  pressedDepth: 0.5,
  idleOffset: 1.5,
  hoverOffset: 3,
};

function threeDButton(base, isLight, hover, pressed) {
  const depth = pressed ? base.pressedDepth : hover ? base.hoverDepth : base.idleDepth;
  const offset = pressed ? 0 : hover ? -base.hoverOffset : -base.idleOffset;
  const scale = pressed ? 0.97 : hover ? 1.025 : 1;
  const shadowTop = isLight ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.16)";
  const shadowBottom = isLight
    ? "rgba(15,23,42,0.22)"
    : "rgba(0,0,0,0.55)";
  const shadowWide = isLight
    ? "rgba(15,23,42,0.18)"
    : "rgba(0,0,0,0.55)";
  return {
    transform: `translateY(${offset}px) scale(${scale})`,
    transition: "transform 120ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 160ms ease",
    boxShadow: `inset 0 1px 0 ${shadowTop}, 0 ${depth}px 0 ${shadowBottom}, 0 ${
      depth + 4
    }px 10px -4px ${shadowWide}`,
  };
}

function TeamChip({ t, count, active, editing, onClick, onRemove, disabledRemove, removeTitle, isLight, C }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [removeHover, setRemoveHover] = useState(false);
  const [removePressed, setRemovePressed] = useState(false);

  const baseBg = active
    ? t.color
    : isLight
    ? "rgba(255,255,255,0.92)"
    : "rgba(20,21,26,0.92)";
  const baseBorder = active
    ? "transparent"
    : hover
    ? isLight
      ? "rgba(15,23,42,0.32)"
      : "rgba(255,255,255,0.32)"
    : isLight
    ? "rgba(15,23,42,0.10)"
    : "rgba(255,255,255,0.10)";
  const baseText = active
    ? "#000000"
    : hover
    ? isLight
      ? "rgba(15,23,42,0.92)"
      : "rgba(255,255,255,0.92)"
    : isLight
    ? "rgba(15,23,42,0.6)"
    : "rgba(255,255,255,0.58)";

  const removeText = active
    ? removeHover
      ? "#000"
      : "rgba(0,0,0,0.5)"
    : removeHover
    ? isLight
      ? "#ef4444"
      : "#ff8f8f"
    : isLight
    ? "rgba(15,23,42,0.4)"
    : "rgba(255,255,255,0.38)";
  const removeBg = removeHover
    ? active
      ? "rgba(0,0,0,0.14)"
      : isLight
      ? "rgba(239,68,68,0.18)"
      : "rgba(255,107,107,0.18)"
    : "transparent";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.6 }}
      transition={{ type: "spring", stiffness: 380, damping: 28 }}
      className="flex items-center gap-1"
    >
      <button
        onClick={onClick}
        aria-pressed={active}
        title={active ? `Clear ${t.name} highlight` : `Highlight ${t.name}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => { setHover(false); setPressed(false); }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        className={cn(
          "flex items-center gap-1 overflow-visible rounded-full border pl-2 text-[9.5px]",
          editing ? "pr-0.5 py-0.5" : "pr-2 py-0.5"
        )}
        style={{
          background: baseBg,
          borderColor: baseBorder,
          color: baseText,
          ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
        }}
      >
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{
            background: active ? "#000" : t.color,
            boxShadow: active ? undefined : `0 0 6px ${t.color}80`,
          }}
        />
        {t.name}
        <span
          className="font-mono text-[8.5px] tabular-nums"
          style={{
            color: active
              ? "rgba(0,0,0,0.55)"
              : isLight
              ? "rgba(15,23,42,0.4)"
              : "rgba(255,255,255,0.32)",
          }}
        >
          {count}
        </span>
      </button>
      {editing && (
        <button
          onClick={onRemove}
          disabled={disabledRemove}
          aria-label={`Delete team ${t.name}`}
          title={removeTitle}
          onMouseEnter={() => setRemoveHover(true)}
          onMouseLeave={() => { setRemoveHover(false); setRemovePressed(false); }}
          onMouseDown={() => setRemovePressed(true)}
          onMouseUp={() => setRemovePressed(false)}
          className={cn(
            "flex h-4 w-4 items-center justify-center rounded-full border",
            disabledRemove && "pointer-events-none opacity-30"
          )}
          style={{
            color: removeText,
            background: removeBg,
            borderColor: isLight
              ? "rgba(15,23,42,0.10)"
              : "rgba(255,255,255,0.10)",
            ...threeDButton(
              { idleDepth: 0.75, hoverDepth: 1.5, pressedDepth: 0.25, idleOffset: 0.75, hoverOffset: 1.5 },
              isLight,
              removeHover,
              removePressed
            ),
          }}
        >
          <X size={9} strokeWidth={2.6} />
        </button>
      )}
    </motion.div>
  );
}

function EditToggle({ editing, onClick, isLight, C }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const color = editing
    ? C.accent
    : hover
    ? isLight
      ? "rgba(15,23,42,0.9)"
      : "#ffffff"
    : isLight
    ? "rgba(15,23,42,0.4)"
    : "rgba(255,255,255,0.4)";
  const bg = editing
    ? C.accentBg
    : hover
    ? isLight
      ? "rgba(15,23,42,0.06)"
      : "rgba(255,255,255,0.07)"
    : "transparent";
  return (
    <button
      onClick={onClick}
      aria-pressed={editing}
      title={editing ? "Done editing teams" : "Edit teams"}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="flex h-5 w-5 items-center justify-center rounded-md border"
      style={{
        color,
        background: bg,
        borderColor: editing
          ? C.accentSoftEdge
          : isLight
          ? "rgba(15,23,42,0.10)"
          : "rgba(255,255,255,0.10)",
        ...threeDButton(
          { idleDepth: 1, hoverDepth: 2, pressedDepth: 0.5, idleOffset: 1, hoverOffset: 2 },
          isLight,
          hover,
          pressed
        ),
      }}
    >
      {editing ? <Check size={10.5} strokeWidth={2.6} /> : <Pencil size={10} />}
    </button>
  );
}

function AddPill({ onClick, isLight, C }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <motion.button
      layout
      onClick={onClick}
      title="Add a team"
      aria-label="Add a team"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-[9.5px]"
      style={{
        borderColor: hover
          ? C.accentSoftEdge
          : isLight
          ? "rgba(15,23,42,0.18)"
          : "rgba(255,255,255,0.2)",
        color: hover
          ? C.accent
          : isLight
          ? "rgba(15,23,42,0.45)"
          : "rgba(255,255,255,0.45)",
        background: isLight ? "rgba(255,255,255,0.92)" : "rgba(20,21,26,0.92)",
        ...threeDButton(BUTTON_3D_BASE, isLight, hover, pressed),
      }}
    >
      <Plus size={10} strokeWidth={2.6} />
      Add
    </motion.button>
  );
}

function ColorSwatch({ color, onClick, isLight }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={onClick}
      title="Cycle team color"
      aria-label="Cycle team color"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="h-3 w-3 shrink-0 rounded-full border"
      style={{
        background: color,
        borderColor: isLight ? "rgba(15,23,42,0.12)" : "rgba(255,255,255,0.16)",
        boxShadow: `0 0 6px ${color}90`,
        transform: `translateY(${pressed ? 0 : hover ? -1.5 : -0.75}px) scale(${
          pressed ? 0.94 : hover ? 1.2 : 1.06
        })`,
        transition: "transform 120ms cubic-bezier(0.2, 0.8, 0.2, 1)",
      }}
    />
  );
}

function SmallIconButton({ label, onClick, color, hoverColor, hoverBg, children, isLight }) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className="flex h-4.5 w-4.5 items-center justify-center rounded-full border"
      style={{
        color: hover && hoverColor ? hoverColor : color,
        background: hover ? hoverBg || "transparent" : "transparent",
        borderColor: isLight ? "rgba(15,23,42,0.10)" : "rgba(255,255,255,0.10)",
        height: "1rem",
        width: "1rem",
        ...threeDButton(
          { idleDepth: 0.75, hoverDepth: 1.5, pressedDepth: 0.25, idleOffset: 0.75, hoverOffset: 1.5 },
          isLight,
          hover,
          pressed
        ),
      }}
    >
      {children}
    </button>
  );
}

export function Legend({ teams, counts, highlight, onHighlight, onAdd, onRemove, isLight, C }) {
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [color, setColor] = useState(TEAM_PALETTE[0]);
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  const commit = () => {
    if (!draft.trim()) {
      setAdding(false);
      return;
    }
    if (onAdd(draft, color)) {
      setDraft("");
      setError(false);
      setColor(TEAM_PALETTE[(TEAM_PALETTE.indexOf(color) + 1) % TEAM_PALETTE.length]);
      inputRef.current?.focus();
    } else {
      setError(true);
      setTimeout(() => setError(false), 1200);
    }
  };

  const cardShadow = isLight
    ? "0 26px 70px -18px rgba(15,23,42,0.35), inset 0 1px 0 rgba(255,255,255,0.95)"
    : "0 30px 80px -20px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.14)";
  const inputBg = isLight ? "rgba(255,255,255,0.9)" : "rgba(20,21,26,0.9)";
  const inputBorder = error
    ? "#ef4444"
    : C.accentSoftEdge;

  const buttonBasePaddingX = "px-2.5";
  const buttonBasePaddingY = "py-0.5";
  const buttonBaseText = "text-[10px]";

  return (
    <div
      className={cn(
        "fixed z-[60] rounded-2xl border backdrop-blur-xl",
        buttonBasePaddingX,
        "py-2",
        "max-w-[min(70vw,640px)]",
        "left-1/2 -translate-x-1/2",
        "bottom-[calc(5rem+32px)]"
      )}
      style={{
        borderColor: isLight ? "rgba(15,23,42,0.09)" : "rgba(255,255,255,0.1)",
        background: isLight ? "rgba(255,255,255,0.92)" : "rgba(14,15,19,0.88)",
        boxShadow: cardShadow,
      }}
    >
      <div className={cn("mb-1 flex items-center justify-between gap-2")}>
        <span
          className={cn("font-mono uppercase tracking-[0.22em]", "text-[7.5px]")}
          style={{ color: isLight ? "rgba(15,23,42,0.45)" : "rgba(255,255,255,0.36)" }}
        >
          Teams
        </span>
        <EditToggle
          editing={editing}
          onClick={() => {
            setEditing((v) => !v);
            setAdding(false);
          }}
          isLight={isLight}
          C={C}
        />
      </div>
      <div className={cn("flex flex-wrap items-center justify-center gap-1", buttonBasePaddingY)}>
        <AnimatePresence initial={false}>
          {teams.map((t) => {
            const active = highlight === t.name;
            const count = counts.get(t.name) ?? 0;
            return (
              <TeamChip
                key={t.name}
                t={t}
                count={count}
                active={active}
                editing={editing}
                onClick={() => onHighlight(active ? null : t.name)}
                onRemove={() => onRemove(t.name)}
                disabledRemove={teams.length <= 1}
                removeTitle={
                  teams.length <= 1
                    ? "At least one team is required"
                    : `Delete ${t.name}${count ? ` — ${count} member(s) move to ${teams[0].name === t.name ? teams[1]?.name : teams[0].name}` : ""}`
                }
                isLight={isLight}
                C={C}
              />
            );
          })}
        </AnimatePresence>

        {adding ? (
          <motion.div
            layout
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className={cn(
              "flex items-center gap-1 rounded-full border pl-1 pr-0.5",
              buttonBasePaddingY
            )}
            style={{
              borderColor: inputBorder,
              background: inputBg,
              ...threeDButton(BUTTON_3D_BASE, isLight, false, false),
            }}
          >
            <ColorSwatch
              color={color}
              onClick={() =>
                setColor(TEAM_PALETTE[(TEAM_PALETTE.indexOf(color) + 1) % TEAM_PALETTE.length])
              }
              isLight={isLight}
            />
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") {
                  setAdding(false);
                  setDraft("");
                }
              }}
              placeholder={error ? "Name taken" : "Team name"}
              maxLength={18}
              className={cn("w-[80px] bg-transparent outline-none", buttonBaseText)}
              style={{
                color: isLight ? "rgba(15,23,42,0.92)" : "rgba(255,255,255,0.92)",
              }}
            />
            <SmallIconButton
              label="Save team"
              onClick={commit}
              color={C.accent}
              hoverColor={C.accent}
              hoverBg={C.accentBgHover}
              isLight={isLight}
            >
              <Check size={10} strokeWidth={3} />
            </SmallIconButton>
            <SmallIconButton
              label="Cancel"
              onClick={() => {
                setAdding(false);
                setDraft("");
              }}
              color={isLight ? "rgba(15,23,42,0.42)" : "rgba(255,255,255,0.42)"}
              hoverColor={isLight ? "rgba(15,23,42,0.9)" : "#ffffff"}
              hoverBg={isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.08)"}
              isLight={isLight}
            >
              <X size={9} strokeWidth={2.6} />
            </SmallIconButton>
          </motion.div>
        ) : (
          <AddPill
            onClick={() => {
              setAdding(true);
              setEditing(true);
            }}
            isLight={isLight}
            C={C}
          />
        )}
      </div>
    </div>
  );
}
